'use client'
import { type FC, useMemo } from 'react'
import { Box, type StackProps, Text, VStack } from '@chakra-ui/react'
import { z } from 'zod'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import {
  FormProvider,
  GrandFormInputField,
  GrandFormSubmitButton,
  type Resolver,
  useForm,
  zodResolver
} from '../grand-form'

export interface MagicLinkFormType {
  email: string
}

export interface MagicLinkFormOverrides {
  magicLinkFormEmail?: string
  magicLinkFormBtnLabel?: string
  magicLinkFormBtnLoading?: string
  magicLinkFormEmailInvalid?: string
}

interface MagicLinkFormProps extends Omit<StackProps, 'onSubmit'> {
  onSubmit: (data: MagicLinkFormType) => Promise<void> | void
  error?: string
  defaultValues?: Partial<MagicLinkFormType>
}

const MagicLinkForm: FC<MagicLinkFormProps> = ({
  onSubmit,
  error,
  defaultValues,
  ...stackProps
}) => {
  const overrides = useOverridesContext<keyof MagicLinkFormOverrides>()
  const emailLabel = overrideText('E-postadress', overrides?.magicLinkFormEmail)
  const btnLabel = overrideText('Skicka', overrides?.magicLinkFormBtnLabel)
  const btnLoading = overrideText('Skickar…', overrides?.magicLinkFormBtnLoading)
  const emailInvalidMsg = overrideText(
    'Ange en giltig e-postadress.',
    overrides?.magicLinkFormEmailInvalid
  )

  const schema = useMemo(() => z.object({ email: z.email(emailInvalidMsg) }), [emailInvalidMsg])

  const methods = useForm<MagicLinkFormType>({
    defaultValues: { email: '', ...defaultValues },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema) as Resolver<MagicLinkFormType>
  })

  const handleSubmit = async (data: MagicLinkFormType) => {
    await onSubmit(data)
    methods.reset({ email: '' })
  }

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={methods.handleSubmit(handleSubmit)} w="full">
        <VStack align="stretch" spacing={4} w="full" {...stackProps}>
          <GrandFormInputField
            control={methods.control}
            label={emailLabel}
            name="email"
            placeholder={emailLabel}
            required
            type="email"
          />
          {error ? (
            <Text color="status.error" fontSize="sm">
              {error}
            </Text>
          ) : null}
          <GrandFormSubmitButton disableIfInvalid={false} loadingText={btnLoading}>
            {btnLabel}
          </GrandFormSubmitButton>
        </VStack>
      </Box>
    </FormProvider>
  )
}

export default MagicLinkForm
